import { nanoid } from 'nanoid';
import type {
  ExerciseDefinition,
  ExerciseInstance,
  Session,
  Week,
  Plan,
  TrainingGoal,
  TrainingLevel,
  ProgressionModel,
} from '../types';

// ── ID generation ───────────────────────────
export const uid = (): string => nanoid();

// ── Factory functions ───────────────────────

export function createExerciseInstance(def: ExerciseDefinition): ExerciseInstance {
  return {
    id: uid(),
    exerciseDefId: def.id,
    name: def.name,
    movementPattern: def.movementPattern,
    primaryMuscle: def.primaryMuscle,
    secondaryMuscles: [...def.secondaryMuscles],
    tag: def.tag,
    sets: def.defaultSets,
    reps: def.defaultReps,
    tempo: def.defaultTempo,
    loadType: 'rpe',
    load: def.defaultRPE,
    rest: def.defaultRest,
    notes: '',
  };
}

export function createSession(day: string = 'Poniedziałek', focus: string = 'Ogólne'): Session {
  return {
    id: uid(),
    day,
    focus,
    warmupExercises: [],
    exercises: [],
  };
}

export function createWeek(weekNumber: number): Week {
  return {
    id: uid(),
    weekNumber,
    sessions: [],
    deload: false,
  };
}

export function createEmptyPlan(overrides?: Partial<Plan>): Plan {
  return {
    id: uid(),
    name: 'Nowy plan',
    goal: 'hypertrophy' as TrainingGoal,
    trainingLevel: 'intermediate' as TrainingLevel,
    mesocycleLength: 4,
    frequency: 4,
    fatigueTolerance: 6,
    progressionModel: 'undulating' as ProgressionModel,
    weeks: [createWeek(1)],
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    ...overrides,
  };
}

// ── Rep parsing ─────────────────────────────

/** Parse rep strings like "8-12", "5", "AMRAP", "30s", "40m" into a numeric average */
export function parseReps(reps: string): number {
  const s = reps.trim().toLowerCase();
  // Duration-based
  if (s.endsWith('s') || s.endsWith('m')) return 10; // treat timed as ~10 for volume calc
  if (s === 'amrap') return 10;
  // Range like "8-12"
  const range = s.match(/^(\d+)\s*-\s*(\d+)$/);
  if (range) return (parseInt(range[1]) + parseInt(range[2])) / 2;
  // per-side
  const perSide = s.match(/^(\d+)\/side$/);
  if (perSide) return parseInt(perSide[1]) * 2;
  // Single number
  const num = parseInt(s);
  return isNaN(num) ? 0 : num;
}

// ── Intensity helpers ───────────────────────

/** Convert RPE to approximate %1RM */
export function rpeToPercent(rpe: number, reps: number): number {
  // Based on Helms RPE table (simplified)
  const base = 100 - (10 - rpe) * 2.5;
  const repDeduction = (reps - 1) * 2.5;
  return Math.max(30, Math.min(100, base - repDeduction));
}

/** Classify intensity level */
export function classifyIntensity(rpe: number): 'low' | 'moderate' | 'high' | 'max' {
  if (rpe <= 6) return 'low';
  if (rpe <= 7.5) return 'moderate';
  if (rpe <= 8.5) return 'high';
  return 'max';
}

// ── Array helpers ───────────────────────────

export function reorder<T>(list: T[], from: number, to: number): T[] {
  const result = [...list];
  const [removed] = result.splice(from, 1);
  result.splice(to, 0, removed);
  return result;
}

// ── Formatting ──────────────────────────────

export function formatRest(seconds: number): string {
  if (seconds >= 60) {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return sec > 0 ? `${min}m ${sec}s` : `${min}m`;
  }
  return `${seconds}s`;
}

export function formatLoad(load: number, type: 'rpe' | 'percent_1rm'): string {
  return type === 'rpe' ? `RPE ${load}` : `${load}%`;
}

// ── Day options ─────────────────────────────
export const DAYS = ['Poniedziałek', 'Wtorek', 'Środa', 'Czwartek', 'Piątek', 'Sobota', 'Niedziela'] as const;

// ── Color utilities ─────────────────────────

export function volumeStatusColor(status: string): string {
  switch (status) {
    case 'below_mev':       return '#6366f1'; // indigo
    case 'at_mev':          return '#3b82f6'; // blue
    case 'optimal':         return '#22c55e'; // green
    case 'approaching_mrv': return '#f59e0b'; // amber
    case 'exceeds_mrv':     return '#ef4444'; // red
    default:                return '#94a3b8'; // slate
  }
}

export function volumeStatusLabel(status: string): string {
  switch (status) {
    case 'below_mev':       return 'Poniżej MEV';
    case 'at_mev':          return 'Na MEV';
    case 'optimal':         return 'Optymalnie';
    case 'approaching_mrv': return 'Blisko MRV';
    case 'exceeds_mrv':     return 'Nad MRV!';
    default:                return '-';
  }
}

export function fatigueColor(score: number): string {
  if (score <= 30) return '#22c55e';
  if (score <= 55) return '#84cc16';
  if (score <= 70) return '#f59e0b';
  if (score <= 85) return '#f97316';
  return '#ef4444';
}

export function qualityScoreColor(score: number): string {
  if (score >= 80) return '#22c55e';
  if (score >= 60) return '#84cc16';
  if (score >= 40) return '#f59e0b';
  return '#ef4444';
}

// ── Clamp / math ────────────────────────────

export function clamp(val: number, min: number, max: number): number {
  return Math.max(min, Math.min(max, val));
}

export function lerp(a: number, b: number, t: number): number {
  return a + (b - a) * clamp(t, 0, 1);
}
